import type {
  AccountTransactionWire,
  AccountTransactionRow,
  AccountTransactionsPayload,
  AccountTransactionsResultSet,
  AccountTransactionsWirePayload,
} from '../account-transactions.model';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function coerceAmount(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

function isTransactionId(value: unknown): value is number | string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return true;
  }
  return typeof value === 'string' && value.trim() !== '';
}

function normalizeTransaction(value: unknown): AccountTransactionWire | null {
  if (!isRecord(value)) {
    return null;
  }
  if (!isTransactionId(value['transactionId'])) {
    return null;
  }
  const createdDate =
    typeof value['createdDate'] === 'string' && value['createdDate'].trim()
      ? value['createdDate']
      : typeof value['date'] === 'string' && value['date'].trim()
        ? value['date']
        : '';
  if (!createdDate) {
    return null;
  }
  const amount = coerceAmount(value['amount']);
  if (amount === null) {
    return null;
  }
  if (typeof value['description'] !== 'string') {
    return null;
  }
  if (typeof value['status'] !== 'string') {
    return null;
  }
  const row: AccountTransactionWire = {
    transactionId: value['transactionId'],
    createdDate,
    amount,
    description: value['description'],
    status: value['status'],
  };
  if (typeof value['type'] === 'string') {
    row.type = value['type'];
  }
  if (typeof value['merchant'] === 'string') {
    row.merchant = value['merchant'];
  }
  if (typeof value['institution'] === 'string') {
    row.institution = value['institution'];
  }
  return row;
}

function isOptionalNonNegativeNumber(value: unknown): boolean {
  return value === undefined || (typeof value === 'number' && Number.isFinite(value) && value >= 0);
}

function isResultSet(value: unknown): value is Record<string, unknown> {
  return (
    isRecord(value) &&
    typeof value['allFetched'] === 'boolean' &&
    isOptionalNonNegativeNumber(value['count']) &&
    isOptionalNonNegativeNumber(value['offset']) &&
    isOptionalNonNegativeNumber(value['limit']) &&
    (value['lastTransactionId'] === undefined || isTransactionId(value['lastTransactionId']))
  );
}

function normalizeResultSet(value: Record<string, unknown>): AccountTransactionsResultSet {
  const last = value['lastTransactionId'];
  return {
    count: typeof value['count'] === 'number' ? value['count'] : undefined,
    offset: typeof value['offset'] === 'number' ? value['offset'] : undefined,
    limit: typeof value['limit'] === 'number' ? value['limit'] : undefined,
    lastTransactionId: isTransactionId(last) ? last : undefined,
    allFetched: value['allFetched'] as boolean,
  };
}

/** True when the outer page and pagination contract are usable. Extra keys allowed. */
export function isAccountTransactionsPayload(
  value: unknown
): value is AccountTransactionsWirePayload {
  if (
    !isRecord(value) ||
    !Array.isArray(value['transactions']) ||
    !isResultSet(value['resultSet'])
  ) {
    return false;
  }
  return true;
}

export function normalizeAccountTransactionsPayload(
  payload: AccountTransactionsWirePayload
): AccountTransactionsPayload {
  const pageKey = payload.resultSet.offset ?? 0;
  const transactions: AccountTransactionRow[] = payload.transactions.map(
    (row, index) => normalizeTransaction(row) ?? {
      unavailable: true,
      rowKey: `unavailable-${pageKey}-${index}`,
    }
  );
  return {
    transactions,
    resultSet: normalizeResultSet(payload.resultSet as unknown as Record<string, unknown>),
  };
}
